<script setup>
import { ref, computed, onMounted, nextTick } from 'vue';
import { useI18n } from 'vue-i18n';
import NextButton from 'dashboard/components-next/button/Button.vue';
import Icon from 'dashboard/components-next/icon/Icon.vue';
import scheduledMessagesAPI from 'dashboard/api/scheduledMessages';
import { useAlert } from 'dashboard/composables';

const props = defineProps({
  conversationId: {
    type: [Number, String],
    required: true,
  },
  messageContent: {
    type: String,
    default: '',
  },
});

const emit = defineEmits(['close', 'scheduled']);

const { t } = useI18n();

const scheduledDate = ref('');
const scheduledTime = ref('');
const content = ref(props.messageContent);
const isScheduling = ref(false);
const attachedFiles = ref([]);
const fileInputRef = ref(null);

const isValid = computed(() => {
  return (
    scheduledDate.value &&
    scheduledTime.value &&
    (content.value.trim().length > 0 || attachedFiles.value.length > 0)
  );
});

const getMinDateTime = () => {
  const now = new Date();
  now.setMinutes(now.getMinutes() + 5);
  return now;
};

const getMinDate = () => {
  const today = new Date();
  const year = today.getFullYear();
  const month = String(today.getMonth() + 1).padStart(2, '0');
  const day = String(today.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

const getMinTime = () => {
  if (scheduledDate.value === getMinDate()) {
    const min = getMinDateTime();
    return `${String(min.getHours()).padStart(2, '0')}:${String(min.getMinutes()).padStart(2, '0')}`;
  }
  return '00:00';
};

onMounted(() => {
  const min = getMinDateTime();
  const year = min.getFullYear();
  const month = String(min.getMonth() + 1).padStart(2, '0');
  const day = String(min.getDate()).padStart(2, '0');
  scheduledDate.value = `${year}-${month}-${day}`;
  scheduledTime.value = `${String(min.getHours()).padStart(2, '0')}:${String(min.getMinutes()).padStart(2, '0')}`;
});

const onClose = () => {
  emit('close');
};

const handleFileSelect = event => {
  const files = Array.from(event.target.files);
  if (attachedFiles.value.length + files.length > 15) {
    useAlert(t('SCHEDULED_MESSAGES.MAX_FILES_ERROR'));
    return;
  }
  attachedFiles.value = [...attachedFiles.value, ...files];
};

const removeFile = index => {
  attachedFiles.value.splice(index, 1);
};

const triggerFileInput = () => {
  fileInputRef.value?.click();
};

const formatFileSize = bytes => {
  if (bytes === 0) return '0 Bytes';
  const k = 1024;
  const sizes = ['Bytes', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return `${Math.round((bytes / k ** i) * 100) / 100} ${sizes[i]}`;
};

const scheduleMessage = async () => {
  if (!isValid.value) return;

  isScheduling.value = true;
  try {
    const dateTimeString = `${scheduledDate.value}T${scheduledTime.value}:00`;
    const scheduledAt = new Date(dateTimeString).toISOString();

    const formData = new FormData();
    formData.append('scheduled_message[content]', content.value || '');
    formData.append('scheduled_message[scheduled_at]', scheduledAt);
    formData.append('scheduled_message[content_type]', 'text');

    // Agregar archivos adjuntos
    attachedFiles.value.forEach(file => {
      formData.append('attachments[]', file);
    });

    await scheduledMessagesAPI.create(props.conversationId, formData);
    useAlert(t('SCHEDULED_MESSAGES.SCHEDULE_SUCCESS'));

    await nextTick();
    emit('scheduled');
    emit('close');
  } catch (error) {
    useAlert(t('SCHEDULED_MESSAGES.SCHEDULE_ERROR'));
  } finally {
    isScheduling.value = false;
  }
};
</script>

<template>
  <div class="flex flex-col">
    <woot-modal-header
      :header-title="$t('SCHEDULED_MESSAGES.TITLE')"
      :header-content="$t('SCHEDULED_MESSAGES.DESCRIPTION')"
    />
    <form
      class="modal-content w-full pt-2 px-5 pb-6"
      @submit.prevent="scheduleMessage"
    >
      <div class="mb-4">
        <label
          class="block text-sm font-medium text-slate-700 dark:text-slate-200 mb-2"
        >
          {{ $t('SCHEDULED_MESSAGES.MESSAGE_LABEL') }}
        </label>
        <textarea
          v-model="content"
          class="w-full px-3 py-2 border border-slate-300 dark:border-slate-600 rounded-md focus:outline-none focus:ring-2 focus:ring-woot-500 dark:bg-slate-800 dark:text-slate-100"
          rows="4"
          :placeholder="$t('SCHEDULED_MESSAGES.MESSAGE_PLACEHOLDER')"
        />
      </div>

      <div class="mb-4">
        <label
          class="block text-sm font-medium text-slate-700 dark:text-slate-200 mb-2"
        >
          {{ $t('SCHEDULED_MESSAGES.ATTACHMENTS_LABEL') }}
        </label>
        <input
          ref="fileInputRef"
          type="file"
          multiple
          class="hidden"
          accept="image/*,video/*,audio/*,.pdf,.doc,.docx,.xls,.xlsx,.txt"
          @change="handleFileSelect"
        />
        <button
          type="button"
          class="flex items-center gap-2 px-4 py-2 text-sm border border-slate-300 dark:border-slate-600 rounded-md hover:bg-slate-50 dark:hover:bg-slate-700 text-slate-700 dark:text-slate-200"
          @click="triggerFileInput"
        >
          <Icon icon="i-material-symbols-attach-file" />
          {{ $t('SCHEDULED_MESSAGES.ADD_ATTACHMENT') }}
        </button>
        <div v-if="attachedFiles.length > 0" class="mt-3 space-y-2">
          <div
            v-for="(file, index) in attachedFiles"
            :key="index"
            class="flex items-center justify-between p-2 bg-slate-50 dark:bg-slate-700 rounded-md"
          >
            <div class="flex items-center gap-2 flex-1 min-w-0">
              <Icon
                icon="i-material-symbols-description"
                class="text-slate-500 dark:text-slate-400"
              />
              <div class="flex-1 min-w-0">
                <p class="text-sm text-slate-700 dark:text-slate-200 truncate">
                  {{ file.name }}
                </p>
                <p class="text-xs text-slate-500 dark:text-slate-400">
                  {{ formatFileSize(file.size) }}
                </p>
              </div>
            </div>
            <button
              type="button"
              class="text-red-500 hover:text-red-700 ml-2"
              @click="removeFile(index)"
            >
              <Icon icon="i-material-symbols-close" />
            </button>
          </div>
        </div>
        <p class="text-xs text-slate-500 dark:text-slate-400 mt-2">
          {{ $t('SCHEDULED_MESSAGES.MAX_FILES_INFO') }}
        </p>
      </div>

      <div class="mb-4">
        <label
          class="block text-sm font-medium text-slate-700 dark:text-slate-200 mb-2"
        >
          {{ $t('SCHEDULED_MESSAGES.DATE_TIME_LABEL') }}
        </label>
        <div class="grid grid-cols-2 gap-3">
          <div>
            <input
              v-model="scheduledDate"
              type="date"
              :min="getMinDate()"
              class="w-full px-3 py-2 border border-slate-300 dark:border-slate-600 rounded-md focus:outline-none focus:ring-2 focus:ring-woot-500 dark:bg-slate-800 dark:text-slate-100"
            />
          </div>
          <div>
            <input
              v-model="scheduledTime"
              type="time"
              :min="getMinTime()"
              class="w-full px-3 py-2 border border-slate-300 dark:border-slate-600 rounded-md focus:outline-none focus:ring-2 focus:ring-woot-500 dark:bg-slate-800 dark:text-slate-100"
            />
          </div>
        </div>
      </div>

      <div class="flex flex-row justify-end w-full gap-2 px-0 py-2 mt-4">
        <NextButton
          faded
          slate
          type="button"
          :label="$t('SCHEDULED_MESSAGES.CANCEL')"
          @click="onClose"
        />
        <NextButton
          type="submit"
          :label="$t('SCHEDULED_MESSAGES.SCHEDULE_BUTTON')"
          :disabled="!isValid || isScheduling"
          :loading="isScheduling"
        />
      </div>
    </form>
  </div>
</template>
